/**
 * Utility functions for consistent date formatting across the application
 */

/**
 * Formats a date string or Date object to a localized date string
 * @param dateInput - Date string, Date object, or null/undefined
 * @param locale - Locale string (default: 'es-ES')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string or fallback text
 */
export const formatDate = (
  dateInput: string | Date | null | undefined,
  locale: string = 'es-ES',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string => {
  if (!dateInput) {
    return 'No disponible'
  }

  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided to formatDate:', dateInput)
      return 'Fecha inválida'
    }

    // Use toLocaleString if time options are provided, otherwise toLocaleDateString
    const hasTimeOptions = options.hour || options.minute || options.second
    return hasTimeOptions
      ? date.toLocaleString(locale, options)
      : date.toLocaleDateString(locale, options)
  } catch (error) {
    console.error('Error formatting date:', error, dateInput)
    return 'Error en fecha'
  }
}
/**
 * Formats a date with time information
 */
export const formatDateTime = (
  dateInput: string | Date | null | undefined,
  locale: string = 'es-ES'
): string => {
  return formatDate(dateInput, locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Formats a date in short format (DD/MM/YYYY)
 */
export const formatDateShort = (
  dateInput: string | Date | null | undefined,
  locale: string = 'es-ES'
): string => {
  return formatDate(dateInput, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}
